import React from "react";
import { Box } from "@mui/material";
import { FlexibleCardLayoutSection as FlexibleCardLayoutSectionType } from "@/types/dto/flexible-card-layout-section";
import { SectionHeader } from "@/components/common/section-header";
import CarouselLayout from "./layouts/carousel-layout";
import ZigzagLayout from "./layouts/zigzag-layout";
import ColumnLayout from "./layouts/column-layout";

export interface FlexibleCardLayoutClientProps {
  section: FlexibleCardLayoutSectionType;
  moduleRankingPosition?: string;
}

const getLayoutName = (layout: string): string => {
  switch (layout) {
    case "carousel":
      return "Carousel";
    case "zigzag":
      return "Zigzag";
    case "column":
      return "Column";
    default:
      return "Card";
  }
};

const FlexibleCardLayoutClient: React.FC<FlexibleCardLayoutClientProps> = ({
  section,
  moduleRankingPosition
}) => (
  <Box
    component="section"
    className="fluid-box anchor-section pattern-bg"
    sx={{ backgroundColor: "background.default" }}
    data-track="visibility"
    data-parent-container-type="flexible-card-layout"
    data-module-name="Flexible Card Layout Section"
    data-ranking-algorithm="manual"
    data-module-ranking="custom_sort"
    data-module-ranking-position={moduleRankingPosition || "10"}
  >
    <Box
      className="destination-page-container"
      sx={{
        padding: {
          zero: "4rem 2.4rem",
          legacyMobile: "4rem 2.4rem",
          legacyTablet: "6rem clamp(2.4rem, 4.5vw, 6.5rem)"
        },
        position: "relative",
        zIndex: 2
      }}
    >
      {/* Skip link for keyboard users */}
      <Box
        component="a"
        href="#after-flexible-cards"
        sx={{
          position: "absolute",
          left: "-9999px",
          "&:focus": {
            position: "static",
            display: "block",
            textAlign: "center",
            marginBottom: "1rem",
            padding: "0.5rem 1rem",
            backgroundColor: "primary.main",
            color: "common.white",
            borderRadius: "0.5rem",
            textDecoration: "none"
          }
        }}
      >
        Skip card section
      </Box>

      <SectionHeader
        headerText={section.basicInfo?.headerText || ""}
        headerTestId="flexible-card-section-header"
        copyTestId="flexible-card-section-subheader"
        copyText={section.basicInfo?.copyText || ""}
        sx={{ marginBottom: "4rem" }}
      />

      <Box
        role="region"
        aria-label={`${getLayoutName(section.layout)} layout with ${section.cards.length} cards`}
      >
        {section.layout === "carousel" && <CarouselLayout cards={section.cards} />}

        {section.layout === "zigzag" && <ZigzagLayout cards={section.cards} />}

        {section.layout === "column" && <ColumnLayout cards={section.cards} />}
      </Box>

      {/* Skip link target */}
      <div id="after-flexible-cards" />
    </Box>
  </Box>
);

export default FlexibleCardLayoutClient;
